import React, { FC, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";

import moment from "moment";
import PaginationForm from "../../../../shared/components/molecules/commons/pagination-form";
Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVjJx26TKEr37c9WBI.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVmJx26TKEr37c9YL5rulw.ttf",
});
// Type definition for PDFDownloadLink render function
interface PDFDownloadLinkRenderProps {
  blob: Blob | null;
  url: string | null;
  loading: boolean;
  error: Error | null;
}

const zw = "\u200B";  

const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

/** ไม่แสดงแถวตำแหน่งสายงานระดับ 3 และ 11 */
function filterEmployeesForRetirementTrendReport(employees: any[] | undefined) {
  if (!employees?.length) return [];
  return employees.filter((item: any) => {
    const n = Number(item.level);
    return n !== 3 && n !== 11;
  });
}

const ReportRetirementTrendNext3YearsOTKListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
    year: moment().year() + 543,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };

  const headers: Header[] = [
    {
      label: "ลำดับ",
      field: "index",
      order: null,
      isOrder: true,
    },
    {
      label: "ตำแหน่งสายงาน",
      field: "level",
      order: null,
      isOrder: true,
    },
    {
      label: "กรอบอัตรากำลัง",
      field: "total_power_rate",
      order: null,
      isOrder: true,
    },
    {
      label: "บรรจุจริง",
      field: "actual_filled",
      order: null,
      isOrder: true,
    },
    {
      label: "ตำแหน่งว่าง",
      field: "vacant_positions",
      order: null,
      isOrder: true,
    },
    {
      label: (moment().year() + 543).toString(),
      field: "retire_current",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 544),
      field: "retire_next1",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 545),
      field: "retire_next2",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result =
          await HrOnlineService.getReportRetirementTrendNext3YearsOTKList(
            params
          );
        const resultAll =
          await HrOnlineService.getReportRetirementTrendNext3YearsOTKList({
            ...params,
            page_size: 10000,
            page: 1,
          });
        setData(result);
        setDataExport(resultAll);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering_field, queryParams.ordering]
  );

  const tableRowNoData = () => {
    if (data.rows?.employee?.length === 0) {
      return <TableRowNoData colspan={7} />;
    }
    return null;
  };

  const permanentSummed = useMemo(() => {
    const arr = data.rows?.permanent ?? [];
    return arr.reduce(
      (acc: any, item: any) => ({
        total_power_rate: acc.total_power_rate + (Number(item.total_power_rate) || 0),
        actual_filled: acc.actual_filled + (Number(item.actual_filled) || 0),
        vacant_positions: acc.vacant_positions + (Number(item.vacant_positions) || 0),
        retire_current: acc.retire_current + (Number(item.retire_current) || 0),
        retire_next1: acc.retire_next1 + (Number(item.retire_next1) || 0),
        retire_next2: acc.retire_next2 + (Number(item.retire_next2) || 0),
      }),
      { total_power_rate: 0, actual_filled: 0, vacant_positions: 0, retire_current: 0, retire_next1: 0, retire_next2: 0 }
    );
  }, [data.rows?.permanent]);

  const employeesVisibleForReport = useMemo(
    () => filterEmployeesForRetirementTrendReport(data.rows?.employee),
    [data.rows?.employee]
  );

  const PDF: React.FC<{ data: any }> = ({ data }) => {
    const pdfPermanentSummed = (() => {
      const arr = data.rows?.permanent ?? [];
      return arr.reduce(
        (acc: any, item: any) => ({
          total_power_rate: acc.total_power_rate + (Number(item.total_power_rate) || 0),
          actual_filled: acc.actual_filled + (Number(item.actual_filled) || 0),
          vacant_positions: acc.vacant_positions + (Number(item.vacant_positions) || 0),
          retire_current: acc.retire_current + (Number(item.retire_current) || 0),
          retire_next1: acc.retire_next1 + (Number(item.retire_next1) || 0),
          retire_next2: acc.retire_next2 + (Number(item.retire_next2) || 0),
        }),
        { total_power_rate: 0, actual_filled: 0, vacant_positions: 0, retire_current: 0, retire_next1: 0, retire_next2: 0 }
      );
    })();
    return (
    <Document>
      <Page size="A4" style={{ padding: 20, fontFamily: "kanit-regular" }}>
        <View>
          {/* หัวรายงาน */}
          <View style={{ marginBottom: 20 }}>
            <Text
              style={{
                fontSize: 16,
                textAlign: "center",
                fontFamily: "kanit-regular",
                marginBottom: 1,
              }}
            >
              {"องค์การตลาดเพื่อเกษตรกร" + zw}
            </Text>

            <Text
              style={{
                fontSize: 16,
                textAlign: "center",
                marginBottom: 1,
                fontFamily: "kanit-regular",
              }}
            >
              {"รายงานแนวโน้มการเกษียณอายุในปีถัดไป 3 ปี อ.ต.ก." + zw}
            </Text>

            <Text
              style={{
                fontSize: 16,
                textAlign: "center",
                marginBottom: 1,
                fontFamily: "kanit-regular",
              }}
            >
              {"ประจำวันที่" + zw}{" "}
              {moment().format("D") +
                " " +
                thaiMonths[moment().month()] +
                " " +
                (moment().year() + 543) +
                zw}
            </Text>
          </View>

          {/* ตาราง - ครอบทั้งตารางด้วย View เดียว */}
          <View
            style={{
              border: "1px solid black",

            }}
          >
            {/* หัวตาราง - คอลัมน์ 1-5 รวม 2 แถว ข้อความอยู่ตรงกลาง, คอลัมน์ปีเกษียณมี 2 แถว แยกกัน */}
            <View
              style={{
                flexDirection: "row",
                borderBottom: "1px solid black",
                fontFamily: "kanit-regular",
                fontSize: 11,
              }}
              fixed
            >
              {/* คอลัมน์ 1-5: รวม 2 แถว ข้อความอยู่ตรงกลาง ไม่มีเส้นคั่นบนล่าง */}
              <View
                style={{
                  width: "8%",
                  justifyContent: "center",
                  paddingVertical: 4,
                  paddingHorizontal: 2,
                  borderRight: "1px solid black",
                }}
              >
                <Text style={{ fontSize: 11, textAlign: "center" }}>
                  {"ลำดับ" + zw}
                </Text>
              </View>
              <View
                style={{
                  width: "22%",
                  justifyContent: "center",
                  paddingVertical: 4,
                  paddingHorizontal: 2,
                  borderRight: "1px solid black",
                }}
              >
                <Text style={{ fontSize: 11, textAlign: "center" }}>
                  {"ตำแหน่งสายงาน" + zw}
                </Text>
              </View>
              <View
                style={{
                  width: "16%",
                  justifyContent: "center",
                  paddingVertical: 4,
                  paddingHorizontal: 2,
                  borderRight: "1px solid black",
                }}
              >
                <Text style={{ fontSize: 11, textAlign: "center" }}>
                  {"กรอบอัตรากำลัง" + zw}
                </Text>
              </View>
              <View
                style={{
                  width: "9%",
                  justifyContent: "center",
                  paddingVertical: 4,
                  paddingHorizontal: 2,
                  borderRight: "1px solid black",
                }}
              >
                <Text style={{ fontSize: 11, textAlign: "center" }}>
                  {"บรรจุจริง" + zw}
                </Text>
              </View>
              <View
                style={{
                  width: "11%",
                  justifyContent: "center",
                  paddingVertical: 4,
                  paddingHorizontal: 2,
                  borderRight: "1px solid black",
                }}
              >
                <Text style={{ fontSize: 11, textAlign: "center" }}>
                  {"ตำแหน่งว่าง" + zw}
                </Text>
              </View>
              {/* คอลัมน์ปีเกษียณ: 2 แถว มีเส้นคั่นระหว่างกัน (34% = 11+11+12 เพื่อรวม 100%) */}
              <View style={{ width: "34%", flexDirection: "column" }}>
                <Text
                  style={{
                    fontSize: 11,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    borderBottom: "1px solid black",
                  }}
                >
                  {"ปีเกษียณ" + zw}
                </Text>
                <View
                  style={{
                    flexDirection: "row",
                    fontFamily: "kanit-regular",
                    fontSize: 11,
                  }}
                >
                  <Text
                    style={{
                      width: "33.33%",
                      fontSize: 11,
                      textAlign: "center",
                      paddingVertical: 4,
                      paddingHorizontal: 2,
                      borderRight: "1px solid black",
                    }}
                  >
                    {(moment().year() + 543) + zw}
                  </Text>
                  <Text
                    style={{
                      width: "33.33%",
                      fontSize: 11,
                      textAlign: "center",
                      paddingVertical: 4,
                      paddingHorizontal: 2,
                      borderRight: "1px solid black",
                    }}
                  >
                    {(moment().year() + 544) + zw}
                  </Text>
                  <Text
                    style={{
                      width: "33.34%",
                      fontSize: 11,
                      textAlign: "center",
                      paddingVertical: 4,
                      paddingHorizontal: 2,
                    }}
                  >
                    {(moment().year() + 545) + zw}
                  </Text>
                </View>
              </View>
            </View>
            {/* ข้อมูลพนักงาน */}
            {(() => {
              const personnelRows = filterEmployeesForRetirementTrendReport(
                data.rows?.employee,
              );
              const getLevelGroup = (level: string) => {
                if (String(level).startsWith("4-7")) return "4-7";
                if (String(level).startsWith("4-5")) return "4-5";
                return String(level);
              };
              return personnelRows.map((item: any, index: number) => {
                const next = personnelRows[index + 1];
                const group = getLevelGroup(item.level);
                const nextGroup = next ? getLevelGroup(next.level) : null;
                const useDashedBorder =
                  !!next &&
                  group === nextGroup &&
                  (group === "4-7" || group === "4-5");
                return (
                  <View
                    key={index}
                    style={{
                      flexDirection: "row",
                      borderBottom: useDashedBorder
                        ? "1px dashed black"
                        : "1px solid black",
                      fontFamily: "kanit-200",
                      fontSize: 10,
                    }}
                  >
                    <Text
                      style={{
                        width: "8%",
                        textAlign: "center",
                        paddingVertical: 4,
                        paddingHorizontal: 2,
                        borderRight: "1px solid black",
                      }}
                    >
                      {index + 1 + zw}
                    </Text>
                    <Text
                      style={{
                        width: "22%",
                        textAlign: "left",
                        paddingVertical: 4,
                        paddingHorizontal: 2,
                        borderRight: "1px solid black",
                      }}
                    >
                      {"ระดับ " + item.level + zw}
                    </Text>
                    <Text
                      style={{
                        width: "16%",
                        textAlign: "center",
                        paddingVertical: 4,
                        paddingHorizontal: 2,
                        borderRight: "1px solid black",
                      }}
                    >
                      {item.total_power_rate + zw}
                    </Text>
                    <Text
                      style={{
                        width: "9%",
                        textAlign: "center",
                        paddingVertical: 4,
                        paddingHorizontal: 2,
                        borderRight: "1px solid black",
                      }}
                    >
                      {item.actual_filled}
                    </Text>
                    <Text
                      style={{
                        width: "11%",
                        textAlign: "center",
                        paddingVertical: 4,
                        paddingHorizontal: 2,
                        borderRight: "1px solid black",
                      }}
                    >
                      {item.vacant_positions}
                    </Text>
                    <Text
                      style={{
                        width: "11.33%",
                        textAlign: "center",
                        paddingVertical: 4,
                        paddingHorizontal: 2,
                        borderRight: "1px solid black",
                      }}
                    >
                      {item.retire_current}
                    </Text>
                    <Text
                      style={{
                        width: "11.33%",
                        textAlign: "center",
                        paddingVertical: 4,
                        paddingHorizontal: 2,
                        borderRight: "1px solid black",
                      }}
                    >
                      {item.retire_next1}
                    </Text>
                    <Text
                      style={{
                        width: "11.34%",
                        textAlign: "center",
                        paddingVertical: 4,
                        paddingHorizontal: 2,
                      }}
                    >
                      {item.retire_next2}
                    </Text>
                  </View>
                );
              });
            })()}

            {/* ลูกจ้างประจำ */}
            {data.rows?.permanent?.length > 0 && (
              <View
                style={{
                  flexDirection: "row",
                  fontFamily: "kanit-200",
                  fontSize: 10,
                  borderBottom: "1px solid black",
                }}
              >
                <Text
                  style={{
                    width: "8%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    borderRight: "1px solid black",
                  }}
                >
                  {(filterEmployeesForRetirementTrendReport(data.rows?.employee)
                    .length + 1) + zw}
                </Text>
                <Text
                  style={{
                    width: "22%",
                    fontSize: 10,
                    textAlign: "left",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    borderRight: "1px solid black",
                  }}
                >
                  {"ลูกจ้างประจำ" + zw}
                </Text>
                <Text
                  style={{
                    width: "16%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    borderRight: "1px solid black",
                  }}
                >
                  {pdfPermanentSummed.total_power_rate}
                </Text>
                <Text
                  style={{
                    width: "9%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    borderRight: "1px solid black",
                  }}
                >
                  {pdfPermanentSummed.actual_filled}
                </Text>
                <Text
                  style={{
                    width: "11%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    borderRight: "1px solid black",
                  }}
                >
                  {pdfPermanentSummed.vacant_positions}
                </Text>
                <Text
                  style={{
                    width: "11.33%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    borderRight: "1px solid black",
                  }}
                >
                  {pdfPermanentSummed.retire_current}
                </Text>
                <Text
                  style={{
                    width: "11.33%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    borderRight: "1px solid black",
                  }}
                >
                  {pdfPermanentSummed.retire_next1}
                </Text>
                <Text
                  style={{
                    width: "11.34%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                  }}
                >
                  {pdfPermanentSummed.retire_next2}
                </Text>
              </View>
            )}
            {/* {data.rows?.temporary?.map((item: any, index: any) => (
              <View key={index} style={{ flexDirection: "row", padding: 3 }}>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                ></Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "20%",
                    fontSize: 8,
                    textAlign: "center",
                    paddingLeft: 1,
                  }}
                >
                  ลูกจ้างชั่วคราว
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item?.total_power_rate ? item?.total_power_rate : 0}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item?.actual_filled ? item?.actual_filled : 0}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item?.vacant_positions ? item?.vacant_positions : 0}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "13%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item?.retire_current ? item?.retire_current : 0}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "13%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item?.retire_next1 ? item?.retire_next1 : 0}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "14%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item?.retire_next2 ? item?.retire_next2 : 0}
                </Text>
              </View>
            ))} */}
            {/* รวม */}
            {data.rows?.total_all?.map((item: any, index: any) => (
              <View
                key={index}
                style={{
                  flexDirection: "row",
                  fontFamily: "kanit-regular",
                  fontSize: 10,
                }}
              >
                <Text
                  style={{
                    width: "8%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    borderRight: "1px solid black",
                  }}
                ></Text>
                <Text
                  style={{
                    width: "22%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  รวม
                </Text>
                <Text
                  style={{
                    width: "16%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.total_power_rate}
                </Text>
                <Text
                  style={{
                    width: "9%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.actual_filled}
                </Text>
                <Text
                  style={{
                    width: "11%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.vacant_positions}
                </Text>
                <Text
                  style={{
                    width: "11.33%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.retire_current}
                </Text>
                <Text
                  style={{
                    width: "11.33%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.retire_next1}
                </Text>
                <Text
                  style={{
                    width: "11.34%",
                    fontSize: 10,
                    textAlign: "center",
                    paddingVertical: 4,
                    paddingHorizontal: 2,
                    fontWeight: "bold",
                  }}
                >
                  {item.retire_next2}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
  };

  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData, queryParams]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">
          รายงานแนวโน้มการเกษียณอายุในปีถัดไป 3 ปี อ.ต.ก.
        </h2>
      </div>
      {loaderComponent}
      <div className="row">
        {/* <label className="form-label">ปี</label>
        <div className="col-sm-12 col-md-2">
          <input
            className="form-control"
            type="text"
            value={queryParams.year ? queryParams.year : ""}
            onChange={handleYearChange}
            name="search"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Button
            variant="primary"
            type="button"
            className="d-inline-block px-4"
            onClick={() => fetchData(queryParams)}
          >
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </Button>
        </div> */}
        <div className="col-sm-12 col-md-12 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานแนวโน้มการเกษียณอายุในปีถัดไป 3 ปี อ.ต.ก.${queryParams.year}.pdf`}
          >
            {
              (({ loading }: any) => (
                <Button
                  variant="primary"
                  className="btn-action btn-mof-primary me-2"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  {loading ? "Loading..." : "Export"}
                </Button>
              )) as any
            }
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {employeesVisibleForReport.map((data: any, index: any) => {
              return (
                <ReportRetirementTrendNext3YearsRow
                  key={index}
                  {...data}
                  index={index + 1}
                />
              );
            })}
            {data.rows?.permanent?.length > 0 && (
              <ReportRetirementTrendNext3YearsRow
                key="permanent"
                index={employeesVisibleForReport.length + 1}
                type="ลูกจ้างประจำ"
                {...permanentSummed}
              />
            )}
            {/* {data.rows?.temporary?.map((data: any, index: any) => {
              return (
                <ReportRetirementTrendNext3YearsRow
                  key={index}
                  {...data}
                  index={index + 1}
                  type="ลูกจ้างชั่วคราว"
                />
              );
            })} */}

            {data.rows?.total_all?.map((data: any, index: any) => {
              return (
                <ReportRetirementTrendNext3YearsRow
                  key={index}
                  {...data}
                  index={index + 1}
                  type="รวม"
                  bold={true}
                />
              );
            })}
            {tableRowNoData()}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const ReportRetirementTrendNext3YearsRow: FC<any> = ({
  index,
  level,
  total_power_rate,
  actual_filled,
  vacant_positions,
  retire_current,
  retire_next1,
  retire_next2,
  type = null,
  bold = false,
}) => {
  return (
    <tr style={{ fontWeight: bold ? "bold" : "normal" }}>
      <td>{type === 'ลูกจ้างประจำ' ? index : type === 'รวม' ? '' : index}</td>
      <td>{ type ? type : "ระดับ "+ level + zw}</td>
      <td>{total_power_rate + zw}</td>
      <td>{actual_filled + zw}</td>
      <td>{vacant_positions + zw}</td>
      <td>{retire_current + zw}</td>
      <td>{retire_next1 + zw}</td>
      <td>{retire_next2 + zw}</td>
    </tr>
  );
};

export default ReportRetirementTrendNext3YearsOTKListPage;
